import React, { useMemo, useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import { currencyString } from '@sales-center/common'

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import IconButton from '@mui/material/IconButton'
import { ILeadOppProduct } from '@user/store/leadOppProducts'
import { IServiceFrequency } from '@user/store/serviceFrequency'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

interface Props {
  service: ILeadOppProduct
  agreementServices: (ILeadOppProduct & { disabled?: boolean })[]
  onChangeService: (service: ILeadOppProduct) => void
  serviceFrequencies: IServiceFrequency[]
  draggable?: boolean
}

export function ServiceSectionDetails(props: Props) {
  const {
    service,
    agreementServices,
    onChangeService,
    serviceFrequencies,
    draggable = false,
  } = props
  const [open, setOpen] = useState<boolean>(false)
  const frequency = useMemo(() => {
    return serviceFrequencies.find((x) => x.id == service.frequencyId)
  }, [service.frequencyId, serviceFrequencies])
  const tidalThemeActive = useTidalThemeActive()

  const {
    quantity,
    initialPrice,
    initialDiscountType,
    initialDiscountAmount,
    recurringDiscountType,
    recurringDiscountAmount,
    recurringPrice,
  } = service

  const initialDiscount = useMemo(
    () =>
      initialDiscountType === 'dollar'
        ? `$${currencyString(initialDiscountAmount)}`
        : `${initialDiscountAmount * 100}%`,
    [initialDiscountAmount, initialDiscountType],
  )

  const recurringDiscount = useMemo(
    () =>
      recurringDiscountType === 'dollar'
        ? `$${currencyString(recurringDiscountAmount)}`
        : `${recurringDiscountAmount * 100}%`,
    [recurringDiscountAmount, recurringDiscountType],
  )

  const serviceDetailMargin = tidalThemeActive
    ? draggable
      ? '49px'
      : '58px'
    : draggable
      ? '55px'
      : '62px'

  const addedAgreementService = useMemo(() => {
    return agreementServices.find((item) => item.id === service.id)
  }, [agreementServices, service.id])

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <Stack direction="row" alignItems="center">
          {draggable ? (
            <DragIndicatorIcon
              fontSize="medium"
              data-test-id="DragIndicatorIcon"
              sx={{ m: 1.5 }}
            />
          ) : (
            <Checkbox
              checked={!!addedAgreementService}
              disabled={!!addedAgreementService?.disabled}
              onChange={() => onChangeService(service)}
              sx={{ mr: 2 }}
              data-test-id="agreement-card-template-checkbox"
            />
          )}

          <Stack>
            <Typography variant="subtitle3">
              {service.name}
              {' - '}
              <Typography variant="subtitle1" component={'span'}>
                {service.description}
              </Typography>
            </Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle2">
            {' '}
            Total ${currencyString(service.totalFirst)}
          </Typography>
          <IconButton
            aria-label="expand row"
            color="primary"
            onClick={() => setOpen(!open)}
            size="small"
            data-test-id="agreement-card-template-expand-button"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>
      </Stack>

      {open && (
        <Stack ml={serviceDetailMargin} mt={1.5} width="260px">
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Quantity</Typography>
            <Typography variant="subtitle2">{quantity}</Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Frequency</Typography>
            <Typography variant="subtitle2">
              {frequency?.description}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Initial Price</Typography>
            <Typography variant="subtitle2">
              ${currencyString(initialPrice * quantity)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Initial Discount</Typography>
            <Typography variant="subtitle2">{initialDiscount}</Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Recurring Price</Typography>
            <Typography variant="subtitle2">
              ${currencyString(recurringPrice)}
            </Typography>
          </Stack>

          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">Recurring Discount</Typography>
            <Typography variant="subtitle2">{recurringDiscount}</Typography>
          </Stack>
        </Stack>
      )}
    </>
  )
}
